import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, ClipboardPaste } from "lucide-react";
import { parseRateCardPaste, type ParsedRateCardPaste } from "@/lib/parseRateCardPaste";
import type { MarkupTierDraft, PricingDraft } from "@/lib/rateCardPreview";

export type PasteApplyPayload = {
    draft: Partial<PricingDraft>;
    tiers: MarkupTierDraft[];
    trades: string[];
    splitFirelineMain: boolean;
    cityFeesCharged?: boolean;
    adminFeeCharged?: boolean;
    permitFeesCharged?: boolean;
};

type Props = {
    onApply: (payload: PasteApplyPayload) => void;
};

export function PasteImporterDialog({ onApply }: Props) {
    const [open, setOpen] = useState(false);
    const [raw, setRaw] = useState("");
    const parsed = useMemo(() => (raw.trim() ? parseRateCardPaste(raw) : null), [raw]);

    const handleApply = (p: ParsedRateCardPaste) => {
        const trades: string[] = ["backflow"];
        if (p.proposedRules.some((r) => /extinguisher|fire ext/i.test(r.label))) {
            trades.push("fire_ext");
        }
        const positive = (v: number | null) => (v != null && v > 0 ? String(v) : null);
        onApply({
            draft: {
                billing_mode: "time_and_materials",
                hourly_labor_rate: positive(p.hourlyLaborRate),
                hourly_labor_rate_after_hours: positive(p.afterHoursLaborRate),
                test_fee_flat: positive(p.testFeeFlat),
                test_fee_fireline_main: positive(p.testFeeFirelineMain),
                materials_pricing_mode: p.materialTiers.length ? "tiered_markup" : "at_list",
            },
            tiers: p.materialTiers,
            trades: [...new Set(trades)],
            splitFirelineMain:
                p.testFeeFirelineMain != null &&
                p.testFeeFirelineMain > 0 &&
                p.testFeeFirelineMain !== p.testFeeFlat,
            // Presence of a fee exception in the paste = this customer is charged it.
            cityFeesCharged: p.proposedRules.some(
                (r) => r.kind === "category_markup" && /city fee/i.test(r.label),
            ),
            adminFeeCharged: p.proposedRules.some(
                (r) => r.kind === "item_flat" && /test reporting|compliance fee/i.test(r.label),
            ),
            permitFeesCharged: p.proposedRules.some(
                (r) => r.kind === "category_markup" && /permit fee/i.test(r.label),
            ),
        });
        setOpen(false);
        setRaw("");
    };

    return (
        <>
            <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
                <ClipboardPaste className="h-4 w-4 mr-1.5" />
                Paste from ServiceTitan
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)} title="Import from ServiceTitan" className="max-w-2xl">
                <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                        Paste the ServiceTitan <strong>Exceptions</strong> table or <strong>Review &amp; Finish</strong>{" "}
                        screen. We&apos;ll fill the form — review, then click <strong>Save rate card</strong>.
                    </p>
                    <Textarea
                        rows={12}
                        placeholder="Paste ServiceTitan pricing text here…"
                        value={raw}
                        onChange={(e) => setRaw(e.target.value)}
                        className="font-mono text-xs"
                    />
                    {parsed && raw.trim() && (
                        <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                            <p className="text-sm font-medium">Detected values</p>
                            {parsed.warnings.length > 0 && (
                                <div className="flex gap-2 text-amber-600 dark:text-amber-400 text-xs">
                                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                                    <ul className="list-disc pl-4">
                                        {parsed.warnings.map((w, i) => (
                                            <li key={i}>{w}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {parsed.summaryLines.length > 0 ? (
                                <ul className="text-sm space-y-1">
                                    {parsed.summaryLines.map((line, i) => (
                                        <li key={i} className="text-foreground/90">
                                            {line}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-muted-foreground">No structured values found yet.</p>
                            )}
                            <div className="flex justify-end gap-2 pt-2">
                                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => handleApply(parsed)}
                                    disabled={parsed.summaryLines.length === 0}
                                >
                                    Apply to form
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>
        </>
    );
}
